import { Loader } from "@/shared/ui/Loader";
import { Button } from "@/components/ui/button";


import { useNavigate, useSearch } from "@tanstack/react-router";
import { PercentIcon, PlusIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  usePromotionsQuery,
  usePromotionsSummaryQuery,
} from "../api/queries";
import { PromotionsFilter } from "../ui/PromotionsFilter";
import { PromotionsSummaryCards } from "../ui/PromotionsSummaryCards";
import { PromotionsTable } from "../ui/PromotionsTable";

type PromotionsSearch = {
  isActive?: string;
  search?: string;
  offset?: number;
  limit?: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
};

export function PromotionsPage() {
  const nav = useNavigate({ from: "/app/loyalty/promotions" });
  const search = useSearch({ from: "/app/loyalty/promotions" }) as PromotionsSearch;
  const canManage = useActionPermission(["loyalty:update"]);

  const params = {
    isActive:
      search.isActive === "true"
        ? ("true" as const)
        : search.isActive === "false"
          ? ("false" as const)
          : undefined,
    search: search.search,
    offset: search.offset ?? 0,
    limit: search.limit ?? 20,
    sortField: (search.sortField ?? "name") as
      | "name"
      | "startAt"
      | "endAt"
      | "priority",
    sortOrder: (search.sortOrder ?? "asc") as "asc" | "desc",
  };

  const { data, isLoading } = usePromotionsQuery(params);
  const { data: summaryData, isLoading: summaryLoading } =
    usePromotionsSummaryQuery();

  const items = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ໂປຣໂມຊັນ",
              to: "/app/loyalty/promotions",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <PercentIcon className="size-5" />
              ໂປຣໂມຊັນ
            </h2>
            <p className="text-muted-foreground">
              ຈັດການກົດໂປຣໂມຊັນ ເວລາ ແລະ ຂອບເຂດການສົມຄວນ
            </p>
          </div>
          {canManage && (
            <Button
              onClick={() => nav({ to: "/app/loyalty/promotions/create" })}
            >
              <PlusIcon className="size-4" />
              ສ້າງໂປຣໂມຊັນ
            </Button>
          )}
        </div>

        <div className="mb-4">
          <PromotionsSummaryCards
            activeCount={summaryData?.activeCount ?? 0}
            scheduledToday={summaryData?.scheduledToday ?? 0}
            expiringSoon={summaryData?.expiringSoon ?? 0}
            isLoading={summaryLoading}
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <PromotionsFilter />

          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <PromotionsTable
              data={items}
              isLoading={isLoading}
              offset={params.offset ?? 0}
              limit={params.limit ?? 20}
              totalCount={total}
              sortBy={params.sortField}
              sortOrder={params.sortOrder}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sortField: id,
                    sortOrder: desc ? "desc" : "asc",
                  },
                })
              }
              onPaginationChange={(newOffset, newLimit) =>
                nav({
                  search: {
                    ...search,
                    offset: newOffset,
                    limit: newLimit,
                  },
                })
              }
              onEdit={(row) =>
                nav({
                  to: "/app/loyalty/promotions/$promotionId/edit",
                  params: { promotionId: row.id },
                })
              }
            />
          )}
        </div>
      </Main>
    </>
  );
}
